import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface AuditLog {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  resource_name?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  organization_id?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'success' | 'failure' | 'warning';
  metadata: Record<string, any>;
  created_at: string;
}

export interface SecurityEvent {
  id: string;
  event_type:
    | 'login'
    | 'logout'
    | 'failed_login'
    | 'password_change'
    | 'permission_change'
    | 'data_export'
    | 'bulk_operation'
    | 'suspicious_activity';
  user_id?: string;
  user_email?: string;
  ip_address: string;
  user_agent?: string;
  location?: {
    country?: string;
    region?: string;
    city?: string;
  };
  risk_score: number;
  blocked: boolean;
  details: Record<string, any>;
  created_at: string;
}

export interface ComplianceReport {
  id: string;
  report_type: 'gdpr' | 'hipaa' | 'sox' | 'pci' | 'custom';
  period_start: string;
  period_end: string;
  generated_by: string;
  status: 'generating' | 'completed' | 'failed';
  summary: {
    total_records: number;
    data_access_events: number;
    data_modification_events: number;
    user_access_events: number;
    security_incidents: number;
  };
  file_url?: string;
  metadata: Record<string, any>;
  created_at: string;
  completed_at?: string;
}

class AuditLogService {
  // Audit Logging
  async logAction(actionData: {
    action: string;
    resource_type: string;
    resource_id?: string;
    resource_name?: string;
    old_values?: Record<string, any>;
    new_values?: Record<string, any>;
    severity?: AuditLog['severity'];
    status?: AuditLog['status'];
    metadata?: Record<string, any>;
  }): Promise<AuditLog> {
    try {
      // Get current user context
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get user profile for additional info
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Get client information
      const clientInfo = this.getClientInfo();

      const auditData: Omit<AuditLog, 'id' | 'created_at'> = {
        user_id: user.id,
        user_email: user.email || '',
        user_name: profile ? `${profile.first_name} ${profile.last_name}`.trim() : user.email || '',
        action: actionData.action,
        resource_type: actionData.resource_type,
        resource_id: actionData.resource_id,
        resource_name: actionData.resource_name,
        old_values: actionData.old_values,
        new_values: actionData.new_values,
        ip_address: clientInfo.ip_address,
        user_agent: clientInfo.user_agent,
        session_id: clientInfo.session_id,
        organization_id: profile?.organization_id,
        severity:
          actionData.severity ||
          this.calculateSeverity(actionData.action, actionData.resource_type),
        status: actionData.status || 'success',
        metadata: {
          ...actionData.metadata,
          timestamp: new Date().toISOString(),
          client_info: clientInfo,
        },
      };

      const { data, error } = await supabase.from('audit_logs').insert(auditData).select().single();

      if (error) throw error;

      // Check for suspicious patterns
      await this.checkSuspiciousActivity(auditData);

      return data;
    } catch (error) {
      logError('Failed to log audit action:', error);
      throw error;
    }
  }

  private getClientInfo() {
    return {
      ip_address: this.getClientIP(),
      user_agent: navigator.userAgent,
      session_id: this.getSessionId(),
      platform: navigator.platform,
      language: navigator.language,
    };
  }

  private getClientIP(): string {
    // In a real implementation, you'd get this from the server
    // For demo purposes, return a placeholder
    return '192.168.1.1';
  }

  private getSessionId(): string {
    // Get or generate session ID
    let sessionId = sessionStorage.getItem('crm_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('crm_session_id', sessionId);
    }
    return sessionId;
  }

  private calculateSeverity(action: string, resourceType: string): AuditLog['severity'] {
    // High-risk actions
    if (action.includes('delete') || action.includes('bulk_') || action === 'export_data') {
      return 'high';
    }

    // Medium-risk actions
    if (action.includes('update') || action.includes('create') || action === 'access_sensitive') {
      return 'medium';
    }

    // Critical actions
    if (
      action.includes('admin') ||
      action.includes('permission') ||
      resourceType === 'user_management'
    ) {
      return 'critical';
    }

    return 'low';
  }

  private async checkSuspiciousActivity(
    auditData: Omit<AuditLog, 'id' | 'created_at'>
  ): Promise<void> {
    try {
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

      // Check for rapid successive actions (potential bot activity)
      const { data: recentActions } = await supabase
        .from('audit_logs')
        .select('id')
        .eq('user_id', auditData.user_id)
        .gte('created_at', fiveMinutesAgo.toISOString())
        .limit(20);

      if (recentActions && recentActions.length > 15) {
        await this.logSecurityEvent({
          event_type: 'suspicious_activity',
          user_id: auditData.user_id,
          user_email: auditData.user_email,
          ip_address: auditData.ip_address || '',
          user_agent: auditData.user_agent,
          risk_score: 75,
          blocked: false,
          details: {
            reason: 'Rapid successive actions detected',
            action_count: recentActions.length,
            time_window: '5 minutes',
          },
        });
      }

      // Check for unusual IP address
      const { data: userIPs } = await supabase
        .from('audit_logs')
        .select('ip_address')
        .eq('user_id', auditData.user_id)
        .neq('ip_address', auditData.ip_address)
        .gte('created_at', new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .limit(1);

      if (userIPs && userIPs.length > 0) {
        await this.logSecurityEvent({
          event_type: 'suspicious_activity',
          user_id: auditData.user_id,
          user_email: auditData.user_email,
          ip_address: auditData.ip_address || '',
          user_agent: auditData.user_agent,
          risk_score: 50,
          blocked: false,
          details: {
            reason: 'New IP address detected',
            previous_ip: userIPs[0].ip_address,
            current_ip: auditData.ip_address,
          },
        });
      }
    } catch (error) {
      logError('Failed to check suspicious activity:', error);
    }
  }

  // Security Event Logging
  async logSecurityEvent(
    eventData: Omit<SecurityEvent, 'id' | 'created_at'>
  ): Promise<SecurityEvent> {
    const { data, error } = await supabase
      .from('security_events')
      .insert(eventData)
      .select()
      .single();

    if (error) throw error;

    // Send alert if high risk score
    if (eventData.risk_score >= 70) {
      await this.sendSecurityAlert(data);
    }

    return data;
  }

  private async sendSecurityAlert(event: SecurityEvent): Promise<void> {
    // In a real implementation, this would send notifications to security team
  }

  // Query Audit Logs
  async getAuditLogs(filters: {
    user_id?: string;
    resource_type?: string;
    action?: string;
    severity?: AuditLog['severity'];
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> {
    let query = supabase.from('audit_logs').select('*', { count: 'exact' });

    if (filters.user_id) query = query.eq('user_id', filters.user_id);
    if (filters.resource_type) query = query.eq('resource_type', filters.resource_type);
    if (filters.action) query = query.ilike('action', `%${filters.action}%`);
    if (filters.severity) query = query.eq('severity', filters.severity);
    if (filters.start_date) query = query.gte('created_at', filters.start_date);
    if (filters.end_date) query = query.lte('created_at', filters.end_date);

    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 50) - 1);

    if (error) throw error;

    return {
      logs: data || [],
      total: count || 0,
    };
  }

  async getSecurityEvents(filters: {
    event_type?: SecurityEvent['event_type'];
    user_id?: string;
    min_risk_score?: number;
    blocked?: boolean;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ events: SecurityEvent[]; total: number }> {
    let query = supabase.from('security_events').select('*', { count: 'exact' });

    if (filters.event_type) query = query.eq('event_type', filters.event_type);
    if (filters.user_id) query = query.eq('user_id', filters.user_id);
    if (filters.min_risk_score) query = query.gte('risk_score', filters.min_risk_score);
    if (filters.blocked !== undefined) query = query.eq('blocked', filters.blocked);
    if (filters.start_date) query = query.gte('created_at', filters.start_date);
    if (filters.end_date) query = query.lte('created_at', filters.end_date);

    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 50) - 1);

    if (error) throw error;

    return {
      events: data || [],
      total: count || 0,
    };
  }

  // User Activity Tracking
  async getUserActivity(
    userId: string,
    timeframe: 'day' | 'week' | 'month' = 'week'
  ): Promise<any> {
    const startDate = new Date();
    switch (timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
    }

    const { data: logs } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', startDate.toISOString());

    const activities = logs || [];

    return {
      total_actions: activities.length,
      actions_by_type: this.groupBy(activities, 'action'),
      actions_by_resource: this.groupBy(activities, 'resource_type'),
      actions_by_severity: this.groupBy(activities, 'severity'),
      actions_by_day: this.groupActivitiesByDay(activities),
      most_active_hours: this.getMostActiveHours(activities),
      ip_addresses: [...new Set(activities.map((a) => a.ip_address).filter(Boolean))],
      session_count: [...new Set(activities.map((a) => a.session_id).filter(Boolean))].length,
    };
  }

  private groupBy(array: any[], key: string): Record<string, number> {
    return array.reduce((acc, item) => {
      const group = item[key] || 'unknown';
      acc[group] = (acc[group] || 0) + 1;
      return acc;
    }, {});
  }

  private groupActivitiesByDay(activities: AuditLog[]): Record<string, number> {
    return activities.reduce(
      (acc, activity) => {
        const day = activity.created_at.split('T')[0];
        acc[day] = (acc[day] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }

  private getMostActiveHours(activities: AuditLog[]): Record<number, number> {
    const hours = activities.reduce(
      (acc, activity) => {
        const hour = new Date(activity.created_at).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      },
      {} as Record<number, number>
    );

    return Object.fromEntries(
      Object.entries(hours)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
    );
  }

  // Compliance Reporting
  async generateComplianceReport(reportData: {
    report_type: ComplianceReport['report_type'];
    period_start: string;
    period_end: string;
    metadata?: Record<string, any>;
  }): Promise<ComplianceReport> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Create report record
    const { data: report, error } = await supabase
      .from('compliance_reports')
      .insert({
        report_type: reportData.report_type,
        period_start: reportData.period_start,
        period_end: reportData.period_end,
        generated_by: user.id,
        status: 'generating',
        summary: {
          total_records: 0,
          data_access_events: 0,
          data_modification_events: 0,
          user_access_events: 0,
          security_incidents: 0,
        },
        metadata: reportData.metadata || {},
      })
      .select()
      .single();

    if (error) throw error;

    // Generate report asynchronously
    this.generateReportData(report.id, reportData);

    return report;
  }

  private async generateReportData(reportId: string, reportData: any): Promise<void> {
    try {
      // Get audit logs for the period
      const { logs } = await this.getAuditLogs({
        start_date: reportData.period_start,
        end_date: reportData.period_end,
        limit: 10000,
      });

      // Get security events for the period
      const { events } = await this.getSecurityEvents({
        start_date: reportData.period_start,
        end_date: reportData.period_end,
        limit: 1000,
      });

      // Calculate summary
      const summary = {
        total_records: logs.length,
        data_access_events: logs.filter(
          (l) => l.action.includes('read') || l.action.includes('view')
        ).length,
        data_modification_events: logs.filter(
          (l) =>
            l.action.includes('create') ||
            l.action.includes('update') ||
            l.action.includes('delete')
        ).length,
        user_access_events: logs.filter((l) => l.resource_type === 'user_management').length,
        security_incidents: events.filter((e) => e.risk_score >= 50).length,
      };

      // Generate report file (in a real implementation, this would create a PDF/CSV)
      const reportContent = this.generateReportContent(logs, events, summary, reportData);
      const fileUrl = await this.uploadReportFile(reportId, reportContent);

      // Update report with results
      await supabase
        .from('compliance_reports')
        .update({
          status: 'completed',
          summary,
          file_url: fileUrl,
          completed_at: new Date().toISOString(),
        })
        .eq('id', reportId);
    } catch (error) {
      logError('Failed to generate compliance report:', error);

      await supabase
        .from('compliance_reports')
        .update({
          status: 'failed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', reportId);
    }
  }

  private generateReportContent(
    logs: AuditLog[],
    events: SecurityEvent[],
    summary: any,
    reportData: any
  ): string {
    // Generate compliance report content based on type
    switch (reportData.report_type) {
      case 'gdpr':
        return this.generateGDPRReport(logs, events, summary);
      case 'hipaa':
        return this.generateHIPAAReport(logs, events, summary);
      case 'sox':
        return this.generateSOXReport(logs, events, summary);
      default:
        return this.generateGenericReport(logs, events, summary);
    }
  }

  private generateGDPRReport(logs: AuditLog[], events: SecurityEvent[], summary: any): string {
    return JSON.stringify(
      {
        report_type: 'GDPR Compliance Report',
        summary,
        data_processing_activities: logs.filter(
          (l) =>
            l.action.includes('create') ||
            l.action.includes('update') ||
            l.action.includes('delete')
        ),
        data_access_requests: logs.filter((l) => l.action.includes('export')),
        security_incidents: events.filter((e) => e.risk_score >= 50),
        generated_at: new Date().toISOString(),
      },
      null,
      2
    );
  }

  private generateHIPAAReport(logs: AuditLog[], events: SecurityEvent[], summary: any): string {
    return JSON.stringify(
      {
        report_type: 'HIPAA Compliance Report',
        summary,
        phi_access_logs: logs.filter((l) => l.metadata?.contains_phi),
        unauthorized_access_attempts: events.filter((e) => e.event_type === 'failed_login'),
        audit_trail: logs,
        generated_at: new Date().toISOString(),
      },
      null,
      2
    );
  }

  private generateSOXReport(logs: AuditLog[], events: SecurityEvent[], summary: any): string {
    return JSON.stringify(
      {
        report_type: 'SOX Compliance Report',
        summary,
        financial_data_changes: logs.filter(
          (l) => l.resource_type === 'invoice' || l.resource_type === 'payment'
        ),
        admin_activities: logs.filter((l) => l.severity === 'critical'),
        access_control_changes: logs.filter((l) => l.action.includes('permission')),
        generated_at: new Date().toISOString(),
      },
      null,
      2
    );
  }

  private generateGenericReport(logs: AuditLog[], events: SecurityEvent[], summary: any): string {
    return JSON.stringify(
      {
        report_type: 'General Audit Report',
        summary,
        audit_logs: logs,
        security_events: events,
        generated_at: new Date().toISOString(),
      },
      null,
      2
    );
  }

  private async uploadReportFile(reportId: string, content: string): Promise<string> {
    // In a real implementation, this would upload to file storage
    // For demo purposes, return a mock URL
    return `https://reports.example.com/compliance/${reportId}.json`;
  }

  // Analytics
  async getSecurityAnalytics(timeframe: 'day' | 'week' | 'month' = 'week'): Promise<any> {
    const startDate = new Date();
    switch (timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
    }

    const [auditLogs, securityEvents] = await Promise.all([
      this.getAuditLogs({
        start_date: startDate.toISOString(),
        limit: 10000,
      }),
      this.getSecurityEvents({
        start_date: startDate.toISOString(),
        limit: 1000,
      }),
    ]);

    const logs = auditLogs.logs;
    const events = securityEvents.events;

    return {
      total_actions: logs.length,
      total_users: [...new Set(logs.map((l) => l.user_id))].length,
      security_incidents: events.filter((e) => e.risk_score >= 50).length,
      high_risk_events: events.filter((e) => e.risk_score >= 70).length,
      blocked_events: events.filter((e) => e.blocked).length,
      actions_by_severity: this.groupBy(logs, 'severity'),
      actions_by_resource: this.groupBy(logs, 'resource_type'),
      events_by_type: this.groupBy(events, 'event_type'),
      activity_by_day: this.groupActivitiesByDay(logs),
      top_users: this.getTopActiveUsers(logs),
      risk_trends: this.getRiskTrends(events),
    };
  }

  private getTopActiveUsers(
    logs: AuditLog[]
  ): Array<{ user_id: string; user_name: string; action_count: number }> {
    const userCounts = logs.reduce(
      (acc, log) => {
        if (!acc[log.user_id]) {
          acc[log.user_id] = {
            user_id: log.user_id,
            user_name: log.user_name,
            action_count: 0,
          };
        }
        acc[log.user_id].action_count++;
        return acc;
      },
      {} as Record<string, any>
    );

    return Object.values(userCounts)
      .sort((a: any, b: any) => b.action_count - a.action_count)
      .slice(0, 10);
  }

  private getRiskTrends(events: SecurityEvent[]): Record<string, number> {
    return events.reduce(
      (acc, event) => {
        const day = event.created_at.split('T')[0];
        acc[day] = Math.max(acc[day] || 0, event.risk_score);
        return acc;
      },
      {} as Record<string, number>
    );
  }
}

export const auditLogService = new AuditLogService();
